import { CENTINELA_SUPERVISOR_EVENTS } from "../../../features/centinela/types";
import { SENTINEL_BEHAVIORS } from "../types";
import type { SentinelBehaviorId } from "../types";
import type {
  CentinelaConfig,
  CentinelaConfigInput,
  CentinelaAlertType,
  CentinelaKeywordPolicy,
  CentinelaSupervisorEvent,
  CentinelaSupervisorNotices,
  CentinelaUnitConfig,
  CentinelaUnitConfigInput,
} from "../../../features/centinela/types";

const TIME_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

const DEFAULT_QUIET_FROM = "22:00";

const DEFAULT_QUIET_TO = "06:00";

const DEFAULT_SCHEDULE_FROM = "06:00";

const DEFAULT_SCHEDULE_TO = "22:00";

const DEFAULT_TIMEZONE = "America/Mexico_City";

type Unknown = Record<string, unknown>;

const asRecord = (value: unknown): Unknown =>
  value && typeof value === "object" ? (value as Unknown) : {};

const asBoolean = (value: unknown, fallback: boolean) =>
  typeof value === "boolean" ? value : fallback;

const asInt = (value: unknown, fallback: number, min: number, max: number) => {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(Math.round(parsed), min), max);
};

const asTime = (value: unknown, fallback: string) =>
  typeof value === "string" && TIME_PATTERN.test(value) ? value : fallback;

const asText = (value: unknown) => (typeof value === "string" ? value : "");

function unwrapConfig(payload: unknown): Unknown {
  const root = asRecord(payload);
  const nested = root.config;
  return nested && typeof nested === "object" ? (nested as Unknown) : root;
}

export const servedOperationTimezone = (payload: unknown): string | null =>
  asText(asRecord(unwrapConfig(payload).horario_operacion).timezone).trim() || null;

export function mapCentinelaConfig(payload: unknown): CentinelaConfig {
  const root = unwrapConfig(payload);
  const escalation = asRecord(root.escalation);
  const notices = asRecord(escalation.notify_supervisor_on);
  const quiet = asRecord(escalation.supervisor_quiet_hours);
  const schedule = asRecord(root.horario_operacion);

  const notify = CENTINELA_SUPERVISOR_EVENTS.reduce(
    (acc, event: CentinelaSupervisorEvent) => ({
      ...acc,
      [event]: asBoolean(notices[event], event !== "verificacion_ok"),
    }),
    { coaccion: true } as CentinelaSupervisorNotices,
  );

  return {
    escalation: {
      driver_first: asBoolean(escalation.driver_first, true),
      max_driver_attempts: asInt(escalation.max_driver_attempts, 3, 1, 10),
      attempt_delay_min: asInt(escalation.attempt_delay_min, 5, 1, 120),
      notify_supervisor_on: notify,
      supervisor_quiet_hours: {
        enabled: asBoolean(quiet.enabled, false),
        from: asTime(quiet.from, DEFAULT_QUIET_FROM),
        to: asTime(quiet.to, DEFAULT_QUIET_TO),
      },
    },
    ajuste_de_ruta: {
      enabled: asBoolean(asRecord(root.ajuste_de_ruta).enabled, false),
    },
    horario_operacion: {
      desde: asTime(schedule.desde, DEFAULT_SCHEDULE_FROM),
      hasta: asTime(schedule.hasta, DEFAULT_SCHEDULE_TO),
      timezone: asText(schedule.timezone) || DEFAULT_TIMEZONE,
    },
    voice: {
      voice_id: asText(asRecord(root.voice).voice_id),
    },
  };
}

export interface CentinelaAlertSetting {
  enabled: boolean;
  valor: number | null;
}

export function mapCentinelaAlertSettings(
  payload: unknown,
  alertTypes: CentinelaAlertType[],
): Record<string, CentinelaAlertSetting> {
  const served = asRecord(unwrapConfig(payload).alert_types);

  return alertTypes.reduce<Record<string, CentinelaAlertSetting>>((acc, alert) => {
    const row = asRecord(served[alert.id]);
    const fallback = alert.param ? alert.param.default : null;
    const value = typeof row.valor === "number" ? Math.round(row.valor) : fallback;

    acc[alert.id] = {
      enabled: asBoolean(row.enabled, alert.enabled_default),
      valor: alert.param ? value : null,
    };

    return acc;
  }, {});
}

function unwrapUnitConfig(payload: unknown): Unknown {
  const root = asRecord(payload);
  return asRecord(root.unit_config ?? root.config ?? root);
}

export function mapCentinelaUnitConfig(
  payload: unknown,
  deviceId: string,
): CentinelaUnitConfig {
  const unit = unwrapUnitConfig(payload);
  const policy = asRecord(unit.keyword_policy);

  const keyword: CentinelaKeywordPolicy = {
    enabled: asBoolean(policy.enabled, false),
    mode: asText(policy.mode) || "requerida",
    day_word: asText(policy.day_word),
    duress_word: asText(policy.duress_word),
    rotation: asText(policy.rotation) || "diaria",
  };

  const phone = asText(unit.operator_phone);
  const masked = asText(unit.operator_phone_masked);

  return {
    device_id: asText(unit.device_id) || deviceId,
    operator_name: asText(unit.operator_name),
    operator_phone: phone,
    operator_phone_masked: masked,
    has_operator_phone: asBoolean(unit.has_operator_phone, Boolean(phone || masked)),
    enabled: asBoolean(unit.enabled, keyword.enabled),
    custody_profile: asText(unit.perfil_custodia),
    keyword_policy: keyword,
  };
}

export interface CentinelaUnitConfigDraft {
  operator_name: string;
  operator_phone: string;
  enabled: boolean;
  day_word: string;
  duress_word: string;
}

export function toUnitConfigDraft(
  served: CentinelaUnitConfig | undefined,
): CentinelaUnitConfigDraft {
  return {
    operator_name: served?.operator_name ?? "",
    operator_phone: served?.operator_phone ?? "",
    enabled: served?.enabled ?? false,
    day_word: served?.keyword_policy.day_word ?? "",
    duress_word: served?.keyword_policy.duress_word ?? "",
  };
}

export function diffCentinelaConfig(
  served: CentinelaConfig,
  draft: CentinelaConfig,
  payload?: unknown,
): CentinelaConfigInput {
  const stored = unwrapConfig(payload);
  const storedEscalation = asRecord(stored.escalation);
  const storedQuiet = asRecord(storedEscalation.supervisor_quiet_hours);
  const storedRouteAdjust = asRecord(stored.ajuste_de_ruta);

  const input: CentinelaConfigInput = {};
  const escalation: NonNullable<CentinelaConfigInput["escalation"]> = {};
  const servedEscalation = served.escalation;
  const draftEscalation = draft.escalation;

  if (draftEscalation.driver_first !== servedEscalation.driver_first) {
    escalation.driver_first = draftEscalation.driver_first;
  }
  if (draftEscalation.max_driver_attempts !== servedEscalation.max_driver_attempts) {
    escalation.max_driver_attempts = draftEscalation.max_driver_attempts;
  }
  if (draftEscalation.attempt_delay_min !== servedEscalation.attempt_delay_min) {
    escalation.attempt_delay_min = draftEscalation.attempt_delay_min;
  }

  const storedNotices = asRecord(storedEscalation.notify_supervisor_on);

  const notices = CENTINELA_SUPERVISOR_EVENTS.filter(
    (event) =>
      draftEscalation.notify_supervisor_on[event] !==
      servedEscalation.notify_supervisor_on[event],
  );
  if (notices.length > 0) {
    escalation.notify_supervisor_on = notices.reduce(
      (acc, event) => ({ ...acc, [event]: draftEscalation.notify_supervisor_on[event] }),
      { ...storedNotices },
    );
  }

  const servedQuiet = servedEscalation.supervisor_quiet_hours;
  const draftQuiet = draftEscalation.supervisor_quiet_hours;
  const quietChanged =
    draftQuiet.enabled !== servedQuiet.enabled ||
    draftQuiet.from !== servedQuiet.from ||
    draftQuiet.to !== servedQuiet.to;
  if (quietChanged) {
    escalation.supervisor_quiet_hours = { ...storedQuiet, ...draftQuiet };
  }

  if (Object.keys(escalation).length > 0) input.escalation = escalation;

  if (draft.ajuste_de_ruta.enabled !== served.ajuste_de_ruta.enabled) {
    input.ajuste_de_ruta = { ...storedRouteAdjust, enabled: draft.ajuste_de_ruta.enabled };
  }

  const scheduleChanged =
    draft.horario_operacion.desde !== served.horario_operacion.desde ||
    draft.horario_operacion.hasta !== served.horario_operacion.hasta;
  if (scheduleChanged) {
    input.horario_operacion = {
      ...asRecord(stored.horario_operacion),
      desde: draft.horario_operacion.desde,
      hasta: draft.horario_operacion.hasta,
    };
  }

  return input;
}

export function diffCentinelaUnitConfig(
  served: CentinelaUnitConfig | undefined,
  draft: CentinelaUnitConfigDraft,
): CentinelaUnitConfigInput {
  const base = toUnitConfigDraft(served);
  const input: CentinelaUnitConfigInput = {};
  const policy = served?.keyword_policy;

  if (draft.operator_name !== base.operator_name) input.operator_name = draft.operator_name;
  if (draft.operator_phone.trim() && draft.operator_phone !== base.operator_phone) {
    input.operator_phone = draft.operator_phone.trim();
  }
  if (draft.enabled !== base.enabled) input.enabled = draft.enabled;

  if (draft.day_word !== base.day_word || draft.duress_word !== base.duress_word) {
    input.keyword_policy = {
      ...(policy ?? {}),
      day_word: draft.day_word,
      duress_word: draft.duress_word,
    };
  }

  return input;
}

export function isKeywordRequired(served: CentinelaUnitConfig | undefined) {
  return served?.keyword_policy.mode.trim().toLowerCase() === "requerida";
}

export function hasKeywordCollision(draft: CentinelaUnitConfigDraft) {
  const day = draft.day_word.trim().toLowerCase();
  const duress = draft.duress_word.trim().toLowerCase();
  return Boolean(day) && day === duress;
}

export const CENTINELA_UNIT_BEHAVIORS = [
  "autoCall",
  "validateKeyword",
  "silentProtocol",
] as const;

export const CENTINELA_COMPANY_BEHAVIORS = ["escalateSupervisor", "routeAdjust"] as const;

export type CentinelaSourcedBehavior =
  | (typeof CENTINELA_UNIT_BEHAVIORS)[number]
  | (typeof CENTINELA_COMPANY_BEHAVIORS)[number];

const SOURCED_BEHAVIORS: readonly SentinelBehaviorId[] = [
  ...CENTINELA_UNIT_BEHAVIORS,
  ...CENTINELA_COMPANY_BEHAVIORS,
];

export const CENTINELA_UNSOURCED_BEHAVIORS = SENTINEL_BEHAVIORS.filter(
  (id): id is Exclude<SentinelBehaviorId, CentinelaSourcedBehavior> =>
    !SOURCED_BEHAVIORS.includes(id),
);

export type BehaviorState = "on" | "off" | "unknown";

const asOptionalBoolean = (value: unknown) => (typeof value === "boolean" ? value : undefined);

const asOptionalText = (value: unknown) => (typeof value === "string" ? value : undefined);

const asOptionalFilled = (value: unknown) => {
  const text = asOptionalText(value);
  return text === undefined ? undefined : text.trim().length > 0;
};

type Flag = boolean | undefined;

function everyFlag(...flags: Flag[]): BehaviorState {
  if (flags.includes(false)) return "off";
  if (flags.includes(undefined)) return "unknown";
  return "on";
}

function someFlag(flags: Flag[]): BehaviorState {
  if (flags.includes(true)) return "on";
  if (flags.includes(undefined)) return "unknown";
  return "off";
}

export function deriveUnitBehaviors(
  configPayload: unknown,
  unitConfigPayload: unknown,
): Record<CentinelaSourcedBehavior, BehaviorState> {
  const config = unwrapConfig(configPayload);
  const escalation = asRecord(config.escalation);
  const notices = asRecord(escalation.notify_supervisor_on);

  const unit = unwrapUnitConfig(unitConfigPayload);
  const policy = asRecord(unit.keyword_policy);

  const phone = asOptionalFilled(unit.operator_phone);
  const maskedPhone = asOptionalFilled(unit.operator_phone_masked);
  const hasPhone =
    asOptionalBoolean(unit.has_operator_phone) ??
    (phone === undefined && maskedPhone === undefined ? undefined : Boolean(phone || maskedPhone));

  const keywordEnabled = asOptionalBoolean(policy.enabled);
  const unitEnabled = asOptionalBoolean(unit.enabled) ?? keywordEnabled;
  const keywordMode = asOptionalText(policy.mode);

  return {
    autoCall: everyFlag(unitEnabled, hasPhone, asOptionalBoolean(escalation.driver_first)),
    validateKeyword: everyFlag(unitEnabled, keywordEnabled),
    silentProtocol: everyFlag(
      unitEnabled,
      asOptionalFilled(policy.duress_word),
      keywordMode === undefined ? undefined : keywordMode.trim().toLowerCase() !== "ninguna",
    ),
    escalateSupervisor: someFlag(
      CENTINELA_SUPERVISOR_EVENTS.map((event) => asOptionalBoolean(notices[event])),
    ),
    routeAdjust: everyFlag(asOptionalBoolean(asRecord(config.ajuste_de_ruta).enabled)),
  };
}
